if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.activeElement = null;
        this.rebuyObserver = null;
        this.refreshTimer = undefined;
        this.announcementTimer = undefined;
        this.pendingAnnouncement = undefined;
        this.pendingFocusContext = undefined;
        this.refreshRequest = 0;
        this.lastAddedStorageKey = 'traditions:last-added-cart-variant';
        this.handleEscape = (event) => event.code === 'Escape' && this.close();
        this.handleQuantityKeydown = (event) => {
          if (event.key !== 'Enter' || event.isComposing || !event.target.matches('.quantity__input')) return;

          event.preventDefault();
          event.stopPropagation();
          event.target.dispatchEvent(new Event('change', { bubbles: true }));
        };
        this.handleUpsellClick = (event) => this.onUpsellClick(event);
        this.handleCartUpdate = (event) => {
          if (event?.productVariantId) this.rememberLastAddedVariant(event.productVariantId);

          if (event?.source === 'cart-items' && event.announcement) {
            this.scheduleRefresh({
              open: this.classList.contains('active'),
              delay: 80,
              announcement: event.announcement,
              focusContext: event.focusContext
            });
          }
        };
        this.handleRebuyCartAdd = (event) => {
          const variantId = this.getVariantIdFromEvent(event);
          if (variantId) this.rememberLastAddedVariant(variantId);
          this.scheduleRefresh({
            open: true,
            delay: 40,
            announcement: {
              kind: 'added',
              productTitle: this.getProductTitleFromEvent(event),
              quantity: 1
            }
          });
        };
        this.handleRebuyCartChange = () => this.scheduleRefresh({ open: this.classList.contains('active') });
        this.handleSectionLoad = () => this.bindHeaderCartIcon();
      }

      connectedCallback() {
        this.addEventListener('keyup', this.handleEscape);
        this.addEventListener('keydown', this.handleQuantityKeydown);
        this.addEventListener('click', this.handleUpsellClick);
        this.bindOverlay();
        this.bindHeaderCartIcon();
        this.bindRebuySlot();
        this.updateConditionalUpsells();
        document.addEventListener('rebuy:cart.add', this.handleRebuyCartAdd);
        document.addEventListener('rebuy:cart.change', this.handleRebuyCartChange);
        document.addEventListener('shopify:section:load', this.handleSectionLoad);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('keyup', this.handleEscape);
        this.removeEventListener('keydown', this.handleQuantityKeydown);
        this.removeEventListener('click', this.handleUpsellClick);
        document.removeEventListener('rebuy:cart.add', this.handleRebuyCartAdd);
        document.removeEventListener('rebuy:cart.change', this.handleRebuyCartChange);
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
        this.cartUpdateUnsubscriber?.();
        this.rebuyObserver?.disconnect();
        window.clearTimeout(this.refreshTimer);
        window.clearTimeout(this.announcementTimer);
      }

      bindOverlay() {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (!overlay || overlay.dataset.cartDrawerBound === 'true') return;

        overlay.dataset.cartDrawerBound = 'true';
        overlay.addEventListener('click', () => this.close());
      }

      bindHeaderCartIcon() {
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (!cartLink || cartLink.dataset.customCartBound === 'true') return;

        cartLink.dataset.customCartBound = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.open(cartLink);
          },
          true
        );
        cartLink.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() !== 'SPACE') return;
          event.preventDefault();
          this.open(cartLink);
        });
      }

      bindRebuySlot() {
        this.rebuyObserver?.disconnect();

        const recommendations = this.querySelector('.cart-drawer__recommendations');
        const widget = recommendations?.querySelector('[data-rebuy-id]');
        if (!recommendations) return;

        if (!widget) {
          recommendations.classList.remove('has-rebuy-recommendations');
          this.syncRecommendationsVisibility();
          return;
        }

        const updateVisibility = () => {
          const renderedWidget = widget.querySelector('.rebuy-widget');
          const hasRecommendations =
            renderedWidget &&
            !renderedWidget.classList.contains('is-hidden') &&
            renderedWidget.textContent.trim().length > 0;

          recommendations.classList.toggle('has-rebuy-recommendations', Boolean(hasRecommendations));
          this.syncRecommendationsVisibility();
        };

        updateVisibility();
        this.rebuyObserver = new MutationObserver(updateVisibility);
        this.rebuyObserver.observe(widget, {
          attributes: true,
          attributeFilter: ['class', 'style'],
          childList: true,
          subtree: true
        });
      }

      syncRecommendationsVisibility() {
        const recommendations = this.querySelector('.cart-drawer__recommendations');
        if (!recommendations) return;

        const isReady =
          recommendations.classList.contains('has-conditional-upsells') ||
          recommendations.classList.contains('has-rebuy-recommendations');

        recommendations.classList.toggle('is-ready', isReady);
      }

      getVariantIdFromEvent(event) {
        const detail = event?.detail || {};
        return (
          detail.variantId ||
          detail.variant_id ||
          detail.item?.variant_id ||
          detail.item?.variantId ||
          detail.product?.selected_variant?.id ||
          null
        );
      }

      getProductTitleFromEvent(event) {
        const detail = event?.detail || {};
        return (
          detail.productTitle ||
          detail.product_title ||
          detail.item?.product_title ||
          detail.item?.title ||
          detail.product?.title ||
          'Item'
        );
      }

      rememberLastAddedVariant(variantId) {
        if (!variantId) return;

        this.lastAddedVariantId = String(variantId);
        try {
          window.sessionStorage.setItem(this.lastAddedStorageKey, this.lastAddedVariantId);
        } catch (error) {
          // The in-memory value still works when browser storage is unavailable.
        }
      }

      getLastAddedVariant() {
        if (this.lastAddedVariantId) return this.lastAddedVariantId;

        try {
          this.lastAddedVariantId = window.sessionStorage.getItem(this.lastAddedStorageKey);
        } catch (error) {
          this.lastAddedVariantId = null;
        }

        return this.lastAddedVariantId;
      }

      updateConditionalUpsells() {
        const recommendations = this.querySelector('.cart-drawer__recommendations');
        const upsells = recommendations?.querySelector('[data-cart-upsells]');
        if (!recommendations || !upsells) {
          recommendations?.classList.remove('has-conditional-upsells');
          this.syncRecommendationsVisibility();
          return;
        }

        const cartItems = Array.from(this.querySelectorAll('[data-cart-item]'));
        const cartProductIds = new Set(cartItems.map((item) => item.dataset.cartProductId));

        upsells.querySelectorAll('[data-cart-upsell-card]').forEach((card) => {
          card.hidden = cartProductIds.has(card.dataset.cartUpsellProductId);
        });

        const rememberedVariant = this.getLastAddedVariant();
        const lastAddedItem =
          cartItems.find((item) => item.dataset.cartVariantId === rememberedVariant) || cartItems[0];
        const lightingHeading = upsells.querySelector('[data-cart-lighting-heading]');
        if (lightingHeading) {
          const productTitle = lastAddedItem?.dataset.cartProductTitle || '';
          const lightingItem = ['house', 'lantern', 'bucket', 'cottage'].find((word) =>
            new RegExp(`\\b${word}\\b`, 'i').test(productTitle)
          ) || 'item';
          lightingHeading.textContent = `Need to light your ${lightingItem}?`;
        }
        const eligibleGroups = new Set((lastAddedItem?.dataset.cartUpsellGroups || '').split(/\s+/).filter(Boolean));
        let hasVisibleGroup = false;

        upsells.querySelectorAll('[data-cart-upsell-group]').forEach((group) => {
          const hasProducts = Array.from(group.querySelectorAll('[data-cart-upsell-card]')).some(
            (card) => !card.hidden
          );
          const shouldShow = eligibleGroups.has(group.dataset.cartUpsellGroup) && hasProducts;
          group.hidden = !shouldShow;
          hasVisibleGroup ||= shouldShow;
        });

        upsells.hidden = !hasVisibleGroup;
        recommendations.classList.toggle('has-conditional-upsells', hasVisibleGroup);
        this.syncRecommendationsVisibility();
      }

      async onUpsellClick(event) {
        const button = event.target.closest?.('[data-cart-upsell-add]');
        if (!button || button.disabled) return;

        const variantId = button.dataset.variantId;
        const productTitle =
          button.closest('[data-cart-upsell-card]')?.querySelector('.cart-upsell-card__title')?.textContent.trim() ||
          'Item';
        const label = button.querySelector('[data-cart-upsell-button-text]');
        const originalLabel = label?.textContent || 'Add';
        const cartAddUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.classList.add('is-loading');
        if (label) label.textContent = 'Adding…';

        try {
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          const result = await response.json();

          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'This item could not be added.');
          }

          this.rememberLastAddedVariant(variantId);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-upsell',
              productVariantId: variantId
            });
          }
          await this.refresh({
            open: true,
            announcement: { kind: 'added', productTitle, quantity: 1 }
          });
        } catch (error) {
          const errors = this.querySelector('#CartDrawer-CartErrors');
          if (errors) errors.textContent = error.message;
          console.error('Unable to add the cart upsell.', error);
        } finally {
          if (button.isConnected) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            button.classList.remove('is-loading');
            if (label) label.textContent = originalLabel;
          }
        }
      }

      open(triggeredBy, focusContext) {
        if (triggeredBy) this.setActiveElement(triggeredBy);

        this.updateConditionalUpsells();
        document.body.classList.add('custom-cart-visible');
        this.classList.add('animate', 'active');

        const dialog = this.querySelector('.drawer__inner');
        const focusElement = this.getRefreshFocusElement(focusContext) || this.querySelector('.drawer__close') || dialog;

        window.requestAnimationFrame(() => {
          if (dialog && focusElement && typeof trapFocus === 'function') {
            trapFocus(dialog, focusElement);
          } else {
            focusElement?.focus();
          }
        });
      }

      close() {
        this.classList.remove('active');
        document.body.classList.remove('custom-cart-visible');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.activeElement);
        } else {
          this.activeElement?.focus();
        }
      }

      renderContents(parsedState) {
        const addedItem = parsedState.items?.[0] || parsedState;
        this.productId = addedItem.id;
        this.rememberLastAddedVariant(addedItem.id);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        this.refresh({
          open: true,
          announcement: {
            kind: 'added',
            productTitle: addedItem.product_title || addedItem.title || 'Item',
            quantity: Number(addedItem.quantity) || 1
          }
        });
      }

      scheduleRefresh({ open = false, delay = 120, announcement, focusContext } = {}) {
        window.clearTimeout(this.refreshTimer);
        if (announcement) this.pendingAnnouncement = announcement;
        if (focusContext) this.pendingFocusContext = focusContext;

        this.refreshTimer = window.setTimeout(
          () => {
            const queuedAnnouncement = this.pendingAnnouncement;
            const queuedFocusContext = this.pendingFocusContext;
            this.pendingAnnouncement = undefined;
            this.pendingFocusContext = undefined;
            this.refresh({ open, announcement: queuedAnnouncement, focusContext: queuedFocusContext });
          },
          delay
        );
      }

      async refresh({ open = false, announcement, focusContext } = {}) {
        const requestNumber = ++this.refreshRequest;
        const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
        const separator = cartUrl.includes('?') ? '&' : '?';

        try {
          const response = await fetch(`${cartUrl}${separator}section_id=cart-drawer`, {
            headers: { Accept: 'text/html' },
            cache: 'no-store'
          });

          if (!response.ok) throw new Error(`Cart drawer request failed with ${response.status}`);

          const html = await response.text();
          if (requestNumber !== this.refreshRequest) return;

          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const renderedDrawer = documentFragment.querySelector('cart-drawer');
          if (!renderedDrawer) {
            if (announcement) this.announceCartUpdate(announcement);
            return;
          }

          const wasOpen = open || this.classList.contains('active');
          const currentContent = this.querySelector('[data-cart-drawer-content]');
          const renderedContent = renderedDrawer.querySelector('[data-cart-drawer-content]');
          if (!currentContent || !renderedContent) {
            if (announcement) this.announceCartUpdate(announcement, renderedDrawer);
            return;
          }

          currentContent.innerHTML = renderedContent.innerHTML;
          this.dataset.cartItemCount = renderedDrawer.dataset.cartItemCount || '0';
          this.dataset.cartSubtotal = renderedDrawer.dataset.cartSubtotal || '';
          this.classList.toggle('is-empty', renderedDrawer.classList.contains('is-empty'));
          this.bindOverlay();
          this.bindHeaderCartIcon();
          this.bindRebuySlot();
          this.updateConditionalUpsells();

          if (wasOpen) this.open(undefined, focusContext);
          if (announcement) this.announceCartUpdate(announcement, renderedDrawer);
        } catch (error) {
          if (announcement) this.announceCartUpdate(announcement);
          console.error('Unable to refresh the custom cart drawer.', error);
        }
      }

      getRefreshFocusElement(focusContext) {
        if (!focusContext) return null;

        const cartItems = Array.from(this.querySelectorAll('[data-cart-item]'));
        const matchingItem = cartItems.find((item) => item.dataset.cartItemKey === focusContext.itemKey);

        if (matchingItem && focusContext.controlName) {
          const matchingControl = Array.from(matchingItem.querySelectorAll('[name]')).find(
            (control) => control.getAttribute('name') === focusContext.controlName
          );
          if (matchingControl) return matchingControl;
        }

        if (matchingItem) return matchingItem.querySelector('.cart-item__name');

        if (cartItems.length) {
          const fallbackIndex = Math.min(Math.max((focusContext.line || 1) - 1, 0), cartItems.length - 1);
          return cartItems[fallbackIndex].querySelector('.cart-item__name');
        }

        return this.querySelector('.drawer__inner-empty a');
      }

      announceCartUpdate(announcement, renderedDrawer) {
        const liveRegion = document.getElementById('CartDrawer-Status');
        if (!liveRegion) return;

        const productTitle = String(announcement.productTitle || 'Item').trim();
        const quantity = Number(announcement.quantity) || 0;
        const itemCountValue = renderedDrawer?.dataset.cartItemCount;
        const itemCount = itemCountValue === undefined ? null : Number(itemCountValue);
        const subtotal = String(renderedDrawer?.dataset.cartSubtotal || '').trim();
        const shippingSection = renderedDrawer?.querySelector('[data-cart-shipping-progress]');
        const shippingHeading = shippingSection?.querySelector('h3')?.textContent.trim();
        const shippingMessage = shippingSection?.querySelector('[data-cart-shipping-message]')?.textContent.trim();
        const parts = [];

        if (announcement.kind === 'removed') {
          parts.push(`${productTitle} removed from cart.`);
        } else if (announcement.kind === 'quantity') {
          parts.push(`${productTitle} quantity updated to ${quantity}.`);
        } else {
          parts.push(`${productTitle} added to cart.${quantity > 1 ? ` Quantity ${quantity}.` : ''}`);
        }

        if (itemCount === 0) {
          parts.push('Cart is empty.');
        } else if (Number.isFinite(itemCount)) {
          parts.push(`Cart contains ${itemCount} ${itemCount === 1 ? 'item' : 'items'}.`);
          if (subtotal) parts.push(`Subtotal ${subtotal}.`);
        }

        if (shippingHeading && shippingMessage) {
          parts.push(`${shippingHeading}. ${shippingMessage}.`);
        }

        window.clearTimeout(this.announcementTimer);
        liveRegion.textContent = '';
        this.announcementTimer = window.setTimeout(() => {
          liveRegion.textContent = parts.join(' ').replace(/\s+/g, ' ').trim();
        }, 50);
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-drawer',
            selector: '#CartDrawer'
          },
          {
            id: 'cart-icon-bubble'
          }
        ];
      }

      getSectionDOM(html, selector = '.shopify-section') {
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }
  );
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    class CartDrawerItems extends CartItems {
      onCartUpdate() {
        this.closest('cart-drawer')?.scheduleRefresh({
          open: this.closest('cart-drawer')?.classList.contains('active'),
          delay: 80
        });
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section'
          }
        ];
      }
    }
  );
}
